import Intercom from "@intercom/messenger-js-sdk";
import ScreenIndicator from "@/components/screen-indicator";
import { DraggableIntercomButton } from "@/components/draggable-intercom-button";
import { Button } from "@/components/ui/button";
import {
  createRootRouteWithContext,
  Link,
  Outlet,
} from "@tanstack/react-router";
import React from "react";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useSession, useUser } from "@wacht/tanstack-router";
const TanStackRouterDevtools =
  import.meta.env.VITE_NODE_ENV === "production"
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import("@tanstack/router-devtools").then((res) => ({
          default: res.TanStackRouterDevtools,
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel
        })),
      );

const ReactQueryDevtools =
  import.meta.env.VITE_NODE_ENV === "production"
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import("@tanstack/react-query-devtools").then((res) => ({
          default: res.ReactQueryDevtools,
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel
        })),
      );

interface RootContext {
  auth: ReturnType<typeof useSession> | null;
}

export const Route = createRootRouteWithContext<RootContext>()({
  component: RootComponent,
  notFoundComponent: () => {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center h-screen bg-background space-y-4",
        )}
      >
        <p className="text-2xl font-bold text-foreground">Page not found</p>
        <Link to="/">
          <Button>Back to Home </Button>
        </Link>
      </div>
    );
  },
});

function RootComponent() {
  const { user } = useUser();

  // get active organization name and id
  // const { data: organization } = useOrganizationList();

  Intercom({
    app_id: "lsltcn90",
    // hide_default_launcher: true, // Hide the default Intercom launcher button
    // // hide_default_launcher: true, // Hide the default Intercom launcher button
    ...(user && {
      name:
        (user?.first_name && user?.last_name) ??
        `${user?.first_name} ${user?.last_name}`,
      email: user?.primary_email_address?.email ?? "",
    }),
  });

  return (
    <>
      <InternetConnectionIndicator />
      <Outlet />
      <TanStackRouterDevtools position="bottom-left" />
      <ReactQueryDevtools position="right" buttonPosition="bottom-left" />

      <ScreenIndicator />
      {/* <DraggableIntercomButton /> */}
    </>
  );
}

function InternetConnectionIndicator() {
  const { isOnline } = useOnlineStatus();
  const [show, setShow] = React.useState(false);
  const hideTimeout = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (!isOnline) {
      // Show immediately when offline
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
      setShow(true);
    } else {
      // Stay visible for 2s after reconnect
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
      setShow(true);
      hideTimeout.current = setTimeout(() => setShow(false), 2000);
    }
    return () => {
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
    };
  }, [isOnline]);

  return (
    <motion.div
      className={cn(
        "fixed left-0 right-0 bottom-0 z-50 px-6 py-1 rounded shadow-lg transition-all duration-500 text-center text-sm text-white",
        isOnline ? "bg-green-500" : "bg-red-500",
      )}
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: show ? 1 : 0, y: show ? 0 : 100 }}
      transition={{ duration: 0.7, ease: "easeInOut" }}
    >
      {isOnline
        ? "You are connected to the internet."
        : "You are currently offline. Please check your internet connection."}
    </motion.div>
  );
}
